import {
  Component,
  OnInit,
  ViewChild,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppRoutes } from 'src/app/core/models';
import {
  AuthenticationService,
  DefaultsService,
  EquipmentService,
  LocaleService,
  ProfileService,
  SearchService,
} from 'src/app/core/providers';
import { environment } from 'src/environments/environment';
import { DateTime } from 'luxon';
import { SignOutModalComponent } from 'src/app/core/components/sign-out-modal/sign-out-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'src/app/core/dialogs/dialog.service';
import { TYPE as CompetitiveDisclaimerType } from 'src/app/core/disclaimers/competitive-disclaimer-dialog.component';
import { ComponentBase } from '../component.base';
import { NavService } from 'src/app/core/providers/services/nav.service';
import { FiltersService } from 'src/app/filters/filters.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false,
})
export class SettingsComponent extends ComponentBase implements OnInit {
  readonly commissionEstimateButtonText = signal('');
  readonly showEstimates = signal(false);
  hasClientSelection: boolean;
  username: string = '';
  clientName: string = '';
  softwareVersion: string = '';
  hasMultipleClients = false;
  updatedTime: WritableSignal<string> = signal('');

  @ViewChild('signOutModal')
  public SignOutModalComponent: SignOutModalComponent;
  public routes = AppRoutes;

  constructor(
    public defaultsService: DefaultsService,
    public authenticationService: AuthenticationService,
    public equipmentService: EquipmentService,
    public profileService: ProfileService,
    public searchService: SearchService,
    public navService: NavService,
    public translateService: TranslateService,
    public route: ActivatedRoute,
    public router: Router,
    public filtersService: FiltersService,
    private localeService: LocaleService,
    public dialogService: DialogService
  ) {
    super(
      defaultsService,
      authenticationService,
      equipmentService,
      profileService,
      searchService,
      navService,
      translateService,
      route,
      router,
      filtersService
    );
  }

  ngOnInit(): void {
    this.authenticationService.finishSentryTransaction();
    this.username = this.authenticationService.userInfoView.UserName;
    this.clientName =
      this.authenticationService.userInfoView.SelectedClient.ClientShortName;
    this.hasClientSelection =
      this.authenticationService.getSelectableClients().length > 1;
    this.softwareVersion = environment.softwareVersion;

    this.hasMultipleClients =
      this.authenticationService.userInfoView.SelectableClients.filter(
        (r) => r.MQARestricted === false
      ).length > 1;
    this.authenticationService.finishSentryTransaction();

    // The timezone seems to be los angeles time not utc.
    const date = DateTime.fromISO(
      this.authenticationService.userInfoView.SelectedClient.DataLastUpdated?.toString() as string,
      { zone: 'America/Los_Angeles' }
    );

    const showEstimateSetting = this.filterDefaults?.find(
      (r) => r.CodeName === 'mobile.rate.commission-estimate.show'
    );
    this.showEstimates.set(
      showEstimateSetting && showEstimateSetting.DefaultValue === 1
        ? true
        : false
    );

    this.updatedTime.set(date.toString());
    this.commissionEstimateButtonText.set(
      `${this.translateService.instant('main.mobile.estimate_button')} ${this.translateService.instant('main.mobile.rate_type')}`
    );
  }

  getLabelTranslation = (value: string, replacements?: Array<string>) => {
    return this.profileService.getLabelTranslation(value, replacements);
  };

  onSignOutClick = () => {
    this.SignOutModalComponent.showModal();
  };

  showClientSelection = () => {
    return this.hasMultipleClients;
  };

  hasCustomerSelection = () => {
    return this.authenticationService.userInfoView.SelectedClient
      .HasCustomerSelection;
  };

  onNavigate(type: AppRoutes) {
    // Navigation routes should not include that "/" that is part of
    // all of the AppRoutes paths.
    this.router.navigate([type.slice(1)]);
  }

  openDisclaimer() {
    this.dialogService.openDialog(CompetitiveDisclaimerType);
  }
}
